import React, { useState } from "react";
import {
  Box,
  Modal,
  Button,
  Divider,
  Flex,
  Text,
  Table,
  Alert,
  Badge,
  Stack,
} from "@mantine/core";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { UserCounts } from "./";
import { getTimezoneAbbr } from "./helpers";
import { numberFormatter } from "@util/formatters";

export default function BulkCommunicationStatus({ statusInfo, info }) {
  const approved = true;
  const isSms = info.variety === 2;

  // if (!approved && isSms) {
  //   return (
  //     <Alert
  //       color="red"
  //       title="This campaign has not been approved for texting"
  //     >
  //       Please wait for approval before sending a text message campaign.
  //     </Alert>
  //   );
  // }

  return (
    <>
      <Stack gap={10}>
        <Errors items={info.error_data.errors} />
        <div style={{ flexGrow: 1 }}>
          <Badge color="gray" variant="light" size="md" mb="sm">
            Additional Info
          </Badge>
          <Table striped>
            <Table.Tbody>
              {[
                {
                  label: "Variety",
                  value: info.variety_formatted.toUpperCase(),
                },
                {
                  label: "Timezone",
                  value: info.timezone,
                },
                {
                  label: "Messages",
                  value: statusInfo.bulk_communicator_messages_count,
                  show: statusInfo.bulk_communicator_messages_count !== 0,
                },
                {
                  label: "Added Users",
                  value: numberFormatter(info.added_user_count),
                },
              ]
                .filter((f) => (f.show === undefined ? true : f.show))
                .map((item, i) => (
                  <Table.Tr key={i}>
                    <Table.Td fw={600} style={{ width: "150px" }}>
                      {item.label}
                    </Table.Td>
                    <Table.Td>{item.value}</Table.Td>
                  </Table.Tr>
                ))}
            </Table.Tbody>
          </Table>
        </div>
        {statusInfo.enqueued_at && (
          <div style={{ flexGrow: 1 }}>
            <Badge color="gray" variant="light" size="md" mb="sm">
              Important events
            </Badge>
            <Table striped>
              <Table.Tbody>
                {[
                  {
                    label: "Enqueued",
                    value: `${new Date(statusInfo.enqueued_at).toLocaleString(
                      "en-US",
                      {
                        timeZone: info.timezone,
                      }
                    )}${
                      statusInfo.enqueued_by.full_name
                        ? ` by ${statusInfo.enqueued_by.full_name} (${statusInfo.enqueued_by.class_name})`
                        : ""
                    }`,
                    show: true,
                  },
                  {
                    label: "Scheduled",
                    value: `${new Date(statusInfo.scheduled_at).toLocaleString(
                      "en-US",
                      {
                        timeZone: info.timezone,
                      }
                    )}${
                      statusInfo.started_by.full_name
                        ? ` by ${statusInfo.started_by.full_name} (${statusInfo.started_by.class_name})`
                        : ""
                    }`,
                    // show: statusInfo.scheduled_at ? true : false,
                    show: false,
                  },
                  {
                    label: "Started",
                    value: `${new Date(statusInfo.started_at).toLocaleString(
                      "en-US",
                      {
                        timeZone: info.timezone,
                      }
                    )}${
                      statusInfo.started_by.full_name
                        ? ` by ${statusInfo.started_by.full_name} (${statusInfo.started_by.class_name})`
                        : ""
                    }`,
                    show: statusInfo.started_at ? true : false,
                  },
                ]
                  .filter((f) => (f.show === undefined ? true : f.show))
                  .map((item, i) => (
                    <Table.Tr key={i}>
                      <Table.Td fw={600} style={{ width: "150px" }}>
                        {item.label}
                      </Table.Td>
                      <Table.Td>{item.value}</Table.Td>
                    </Table.Tr>
                  ))}
              </Table.Tbody>
            </Table>
          </div>
        )}
        {info.schedule_breakdown && (
          <div style={{ flexGrow: 1 }}>
            <Badge color="gray" variant="light" size="md" mb="sm">
              Send Times
            </Badge>
            <UserCounts
              size="sm"
              timezones={info.schedule_breakdown.map((m) => ({
                ...m,
                available: true,
                their_time: `${new Date(m.scheduled_send_at).toLocaleString(
                  "en-US",
                  {
                    // timeZone: info.timezone,
                    timeZone: m.raw_timezone,
                    hour12: true,
                    timeStyle: "short",
                  }
                )} ${getTimezoneAbbr(m.offset / 60 / 60)}`,
                your_time: `${new Date(m.scheduled_send_at).toLocaleString(
                  "en-US",
                  {
                    timeZone: info.timezone,
                    hour12: true,
                    timeStyle: "short",
                  }
                )} ${getTimezoneAbbr(info.timezone_offset)}`,
              }))}
            />
          </div>
        )}
      </Stack>
    </>
  );
}

const Errors = ({ items = [] }) => {
  const [isOpen, setOpen] = useState(false);

  function onClose() {
    setOpen(false);
  }

  if (items.length === 0) return null;

  const chartDataObject = [...items].reduce((acc, cur) => {
    if (!acc[cur.ErrorCode]) {
      acc[cur.ErrorCode] = {
        name: cur.ErrorCode,
        y: 1,
      };
    } else {
      acc[cur.ErrorCode].y += 1;
    }
    return acc;
  }, {});
  const chartData = Object.keys(chartDataObject).map((m) => ({
    name: m,
    y: chartDataObject[m].y,
  }));

  return (
    <Box mb="xs">
      <Button
        onClick={() => setOpen(true)}
        variant="subtle"
        c="red"
        p={0}
        size="xs"
      >
        We found {items.length} error{items.length !== 1 ? "s" : ""}!
      </Button>
      <Modal opened={isOpen} onClose={onClose}>
        <Text fw={600} size="xs">
          Error Breakdown
        </Text>
        <HighchartsReact
          highcharts={Highcharts}
          options={{
            chart: {
              height: "300px",
              type: "pie",
              style: {
                fontFamily: "inherit",
              },
            },
            title: {
              text: "",
            },
            tooltip: {
              valueSuffix: "",
            },
            subtitle: {
              text: "",
            },
            plotOptions: {
              series: {
                allowPointSelect: true,
                cursor: "pointer",
                dataLabels: [
                  {
                    enabled: true,
                    distance: 20,
                  },
                  {
                    enabled: true,
                    distance: -40,
                    format: "{point.percentage:.1f}%",
                    style: {
                      fontSize: "1.2em",
                      textOutline: "none",
                      opacity: 0.7,
                    },
                    filter: {
                      operator: ">",
                      property: "percentage",
                      value: 10,
                    },
                  },
                ],
              },
            },
            series: [
              {
                name: "Total",
                colorByPoint: true,
                data: chartData,
              },
            ],
            credits: {
              enabled: false,
            },
          }}
        />
        <Divider mt="lg" mb="lg" />
        {items.map((e, i) => (
          <div key={i}>
            <Text size="sm" mb="xs">
              {e.Message}
            </Text>
            <Text size="sm" c="dimmed" align="right">
              Error Code {e.ErrorCode}
            </Text>
            {i < items.length - 1 && <Divider mt="lg" mb="lg" />}
          </div>
        ))}
      </Modal>
    </Box>
  );
};
